import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {APP_QUERY_KEY_MAP} from '../api/app/constant';
import {getAppHelpList} from '../api/app/getAppHelpList';
import {getAppNoticeList} from '../api/app/getAppNoticeList';
import {getAppSettingTastesServiceLang} from '../api/app/getAppSettingTastesServiceLang';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {PagewebError} from '../errors/PagewebError.class';
import {createAnnouncementContainer} from '../graphql/factory/AnnouncementContainer';
import {
  AnnouncementTarget,
  HelpsQuery,
  type HelpsQueryVariables,
  NoticesQuery,
  type NoticesQueryVariables,
  TastesServiceLangQuery,
  type TastesServiceLangQueryVariables,
} from '../graphql/generated/types';

/**
 * App 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const appFactory = {
  /**
   * 도움말 목록 조회
   *
   * ⚠️ Resolver 로직:
   * - args.target || AnnouncementTarget.All (기본값 처리)
   * - createAnnouncementContainer factory로 변환
   */
  helps: (
    variables?: HelpsQueryVariables,
    options?: Omit<
      UseQueryOptions<HelpsQuery, PagewebError, HelpsQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: APP_QUERY_KEY_MAP.helps(variables),
      queryFn: async () => {
        const target = variables?.target || AnnouncementTarget.All;
        const data = await getAppHelpList({target}, fetcherOptions);

        // Adapter의 combine 로직을 여기서 수행
        return {
          helps: createAnnouncementContainer(data),
        };
      },
      ...options,
    }),

  /**
   * 취향 서비스 언어 설정 조회
   */
  tastesServiceLang: (
    variables?: TastesServiceLangQueryVariables,
    options?: Omit<
      UseQueryOptions<
        TastesServiceLangQuery,
        PagewebError,
        TastesServiceLangQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: APP_QUERY_KEY_MAP.tastesServiceLang(variables),
      queryFn: async () => {
        const data = await getAppSettingTastesServiceLang(fetcherOptions);

        // Adapter의 combine 로직을 여기서 수행 (snake_case → camelCase 변환)
        return {
          tastesServiceLang: {
            selectableServiceLang:
              data.tastes_service_lang?.selectable_service_lang || [],
            userServiceLang: data.tastes_service_lang?.user_service_lang,
          },
        };
      },
      ...options,
    }),

  /**
   * 공지 목록 조회
   *
   * ⚠️ Resolver 로직:
   * - args.target || AnnouncementTarget.All (기본값 처리)
   * - createAnnouncementContainer factory로 변환
   */
  notices: (
    variables?: NoticesQueryVariables,
    options?: Omit<
      UseQueryOptions<NoticesQuery, PagewebError, NoticesQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: APP_QUERY_KEY_MAP.notices(variables),
      queryFn: async () => {
        const target = variables?.target || AnnouncementTarget.All;
        const data = await getAppNoticeList(
          {page: 0, size: 10000, target},
          fetcherOptions
        );

        // Adapter의 combine 로직을 여기서 수행
        return {
          notices: createAnnouncementContainer(data),
        };
      },
      ...options,
    }),
} as const;
